import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import toastLib, { type Toast } from "react-hot-toast";

import { cn } from "@/utils/cn";

type ToastTone = "success" | "error" | "info";

const ICON_BY_TONE = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
} as const;

const ICON_COLOR_BY_TONE: Record<ToastTone, string> = {
  success: "text-success",
  error: "text-destructive",
  info: "text-info",
};

export interface ToastAction {
  label: string;
  onClick: () => void;
}

function ToastCard({
  toastInstance,
  tone,
  message,
  action,
}: {
  toastInstance: Toast;
  tone: ToastTone;
  message: string;
  action?: ToastAction;
}) {
  const Icon = ICON_BY_TONE[tone];

  return (
    <div
      className={cn(
        "flex w-full max-w-sm items-start gap-3 rounded-lg bg-card p-4 text-card-foreground shadow-xl transition-all",
        toastInstance.visible
          ? "animate-in fade-in slide-in-from-bottom-2"
          : "animate-out fade-out",
      )}
    >
      <Icon
        className={cn("mt-0.5 h-5 w-5 shrink-0", ICON_COLOR_BY_TONE[tone])}
        aria-hidden="true"
      />
      <p className="flex-1 text-body text-foreground">{message}</p>
      {action && (
        <button
          type="button"
          onClick={() => {
            action.onClick();
            toastLib.dismiss(toastInstance.id);
          }}
          className="shrink-0 text-body-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => toastLib.dismiss(toastInstance.id)}
        className="shrink-0 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * docs/ui/design-system.md §16: leading semantic icon + message + dismiss,
 * `radius-lg`/`shadow-xl`, success/info auto-dismiss at 3s, error requires
 * explicit dismissal (its accessible role is `alert`, announced
 * `aria-live="assertive"`, the others `status`/`polite`). Built on
 * `toast.custom` rather than `toast.success`/`toast.error` because those
 * render react-hot-toast's own default bubble — this is the styled
 * replacement every feature should call instead.
 *
 * `action` is optional and never the only way to do whatever it triggers
 * (e.g. "View Wishlist" — the wishlist is still reachable from the header
 * regardless) — consistent with the doc's "doesn't require the user to
 * act" purpose statement; it's a shortcut, not a requirement.
 */
function show(tone: ToastTone, message: string, action?: ToastAction) {
  return toastLib.custom(
    (t) => <ToastCard toastInstance={t} tone={tone} message={message} action={action} />,
    {
      duration: tone === "error" ? Infinity : 3000,
      ariaProps: {
        role: tone === "error" ? "alert" : "status",
        "aria-live": tone === "error" ? "assertive" : "polite",
      },
    },
  );
}

export const showToast = {
  success: (message: string, action?: ToastAction) => show("success", message, action),
  error: (message: string, action?: ToastAction) => show("error", message, action),
  info: (message: string, action?: ToastAction) => show("info", message, action),
  dismiss: toastLib.dismiss,
};
